const palette = {
  // HolDeinePlakette Brand Colors
  primary: "#FF382F", // Red - CTAs, headers, highlights
  background: "#111E2C", // Dusknight - main background
  surface: "#FFFFFF", // White - cards, primary surfaces
  surfaceSecondary: "#F4F4F4", // Light grey - secondary surfaces

  // Extended palette
  neutral100: "#FFFFFF",
  neutral150: "#F0F0F0", // Footer top border (Tailwind-derived)
  neutral200: "#F4F4F4",
  neutral300: "#E0E0E0",
  neutral350: "#D1D5DB", // Footer separator (Tailwind-derived)
  neutral400: "#BDBDBD",
  neutral450: "#9CA3AF", // Footer text (Tailwind-derived)
  neutral500: "#9E9E9E",
  neutral600: "#666666",
  neutral700: "#424242",
  neutral800: "#111E2C",
  neutral900: "#000000",

  // Semantic colors
  success: "#4CAF50",
  warning: "#FF9800",
  error: "#F44336",

  // Secondary colors for toggles
  secondary500: "#2196F3",
  accent100: "#E1F5FE",

  // Border and dividers
  border: "#E0E0E0",
  divider: "#F4F4F4",

  // Status colors (B2C tracking inspection-card states; distinct from
  // the system-wide success/warning above, which other surfaces rely on)
  statusPassed: "#0F7938", // Dark forest green — Bestanden
  statusPending: "#C2410C", // Burnt orange — Bald verfügbar
  statusCompleted: "#1B5E20", // Forest green — B2B FleetHub "finished" state
  // statusFailed intentionally points to primary (one-red rule)

  // Surface gradients on dark time cards (/track bottom sheet)
  surfaceDarkTop: "#152536", // gradient top stop
  surfaceDarkHover: "#1a2a3e", // dark button hover state (FleetHub header)
  // surfaceDarkBottom = background (#111E2C)

  // Row highlight (animated FleetOrderRow flash)
  highlightYellow: "#FEF3C7",

  // Modal design language (shared across Reschedule/Cancel/Book modals)
  modalScrim: "#F3F4F6", // Tailwind gray-100, modal backdrop scrim
  modalInputBg: "#F9FAFB", // Tailwind gray-50, input field background
  modalBorder: "#E5E7EB", // Tailwind gray-200, modal border/divider
  modalHelperText: "#6B7280", // Tailwind gray-500, helper/label text
  modalSlateText: "#8B95A5", // Bespoke slate for muted body text
  modalBorderSubtle: "#ECEEF2", // Lighter modal border variant

  // Error/success states (modal alerts)
  errorBgLight: "#FEF2F2", // Light pink error background
  errorBgPink: "#FFCDD2", // Deeper pink for cancel-button background tint
  errorBorderLight: "#FECACA", // Subtle pink border around error states
  successBgLight: "#F0FDF4", // Light mint green success background

  // Physical license-plate colors (NumberPlate input defaults — domain meaning,
  // not brand-aligned: these mimic an actual German license plate)
  platePhysicalDark: "#1C1C1E",
  platePhysicalMid: "#2C2C2E",

  // Feedback signal colors (Tailwind-derived; used across pickers + status chips)
  amber: "#F59E0B",
  amberDark: "#D97706",
  emerald: "#10B981",
  emeraldDark: "#059669",
  infoBlue: "#3B82F6",
  infoBlueDark: "#1E40AF",
  infoBgLight: "#EFF6FF",

  // Inspection result badge palette (softer variants distinct from system success/warning)
  badgePassedBg: "#E7F5E8",
  badgePassedText: "#2E7D32",
  badgeFailedBg: "#FFEDEF",
  badgeFailedText: "#C62828",
  badgePendingBg: "#FFF8E1",
  badgePendingText: "#E65100",

  // Tracking/progress UI
  liveBadgeTint: "#FFB1A8", // Light pink-red live indicator
  progressBarIncomplete: "#E8E8E8",
  progressBarComplete: "#757575",

  // FilterBar Apple-style soft greys
  filterBarSurface: "#F5F5F7",
  filterBarSurfaceActive: "#EBEBED",

  // Confetti palette (referral survey celebration)
  confettiYellow: "#FFD700",
  confettiPink: "#FF69B4",
  confettiPurple: "#6C63FF",
  confettiCyan: "#00BCD4",

  // External brand marks (do not modify; owned by the third party)
  whatsAppGreen: "#25D366",

  // Overlays
  overlay20: "rgba(17, 30, 44, 0.2)",
  overlay50: "rgba(17, 30, 44, 0.5)",
  overlay70: "rgba(17, 30, 44, 0.7)",
} as const

export const colors = {
  /**
   * The palette is available to use, but prefer using the name.
   * This is only included for rare, one-off cases. Try to use
   * semantic names as much as possible.
   */
  palette,
  /**
   * A helper for making something see-thru.
   */
  transparent: "rgba(0, 0, 0, 0)",
  /**
   * The default text color in many components.
   */
  text: palette.neutral800,
  /**
   * Text on light backgrounds.
   */
  textInverse: palette.neutral100,
  /**
   * Secondary text information.
   */
  textDim: palette.neutral600,
  /**
   * The default color of the screen background.
   */
  background: palette.background,
  /**
   * Surface color for cards and sheets.
   */
  surface: palette.surface,
  /**
   * Secondary surface color.
   */
  surfaceSecondary: palette.surfaceSecondary,
  /**
   * The default border color.
   */
  border: palette.border,
  /**
   * The main tinting color (brand red).
   */
  tint: palette.primary,
  /**
   * The inactive tinting color.
   */
  tintInactive: palette.neutral300,
  /**
   * A subtle color used for lines.
   */
  separator: palette.divider,
  /**
   * Error messages.
   */
  error: palette.error,
  /**
   * Success states.
   */
  success: palette.success,
  /**
   * Warning states.
   */
  warning: palette.warning,
  /**
   * Overlay backgrounds.
   */
  overlay: palette.overlay70,
  /**
   * Primary brand color.
   */
  primary: palette.primary,
  /**
   * Error background color.
   */
  errorBackground: palette.error,

  /**
   * Inspection-result status colors (B2C tracking card on /track).
   * Failed deliberately collapses to primary (one-red rule).
   */
  statusPassed: palette.statusPassed,
  statusFailed: palette.primary,
  statusPending: palette.statusPending,
  statusCompleted: palette.statusCompleted,

  /**
   * Dark-card gradient stops (time cards in /track bottom sheet)
   * and dark-button hover state (FleetHub header buttons).
   */
  surfaceDarkTop: palette.surfaceDarkTop,
  surfaceDarkBottom: palette.background,
  surfaceDarkHover: palette.surfaceDarkHover,

  /**
   * Animation highlight (FleetOrderRow row-flash).
   */
  highlightYellow: palette.highlightYellow,

  /**
   * Modal design language (Reschedule/Cancel/Book/etc.).
   */
  modalScrim: palette.modalScrim,
  modalInputBg: palette.modalInputBg,
  modalBorder: palette.modalBorder,
  modalHelperText: palette.modalHelperText,
  modalSlateText: palette.modalSlateText,
  modalBorderSubtle: palette.modalBorderSubtle,

  /**
   * Soft error/success backgrounds for modal alerts.
   */
  errorBgLight: palette.errorBgLight,
  errorBgPink: palette.errorBgPink,
  errorBorderLight: palette.errorBorderLight,
  successBgLight: palette.successBgLight,

  /**
   * Physical license-plate defaults (domain colors, not brand).
   */
  platePhysicalDark: palette.platePhysicalDark,
  platePhysicalMid: palette.platePhysicalMid,

  /**
   * Feedback signal colors (status chips, pickers, info banners).
   */
  amber: palette.amber,
  amberDark: palette.amberDark,
  emerald: palette.emerald,
  emeraldDark: palette.emeraldDark,
  infoBlue: palette.infoBlue,
  infoBlueDark: palette.infoBlueDark,
  infoBgLight: palette.infoBgLight,

  /**
   * Inspection result badge palette.
   */
  badgePassedBg: palette.badgePassedBg,
  badgePassedText: palette.badgePassedText,
  badgeFailedBg: palette.badgeFailedBg,
  badgeFailedText: palette.badgeFailedText,
  badgePendingBg: palette.badgePendingBg,
  badgePendingText: palette.badgePendingText,

  /**
   * Tracking + progress UI.
   */
  liveBadgeTint: palette.liveBadgeTint,
  progressBarIncomplete: palette.progressBarIncomplete,
  progressBarComplete: palette.progressBarComplete,

  /**
   * Apple-style soft greys (FilterBar).
   */
  filterBarSurface: palette.filterBarSurface,
  filterBarSurfaceActive: palette.filterBarSurfaceActive,

  /**
   * Confetti celebration palette.
   */
  confettiYellow: palette.confettiYellow,
  confettiPink: palette.confettiPink,
  confettiPurple: palette.confettiPurple,
  confettiCyan: palette.confettiCyan,

  /**
   * External brand marks (used inline; do not modify).
   */
  whatsAppGreen: palette.whatsAppGreen,

  /**
   * Footer-specific neutrals (Tailwind-derived greys kept exact).
   */
  footerBorder: palette.neutral150,
  footerSeparator: palette.neutral350,
  footerText: palette.neutral450,
} as const
